import * as React from 'react';
import { useCallback, useContext, useEffect, useRef } from 'react';

import { isEmpty } from 'lodash';

import type { Props as CmlEditorProps } from 'bundles/authoring/content-authoring/components/CMLEditor';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import CmlEditorDropdown from 'bundles/grading/components/staff-graded-assignments/reusable-comments/CmlEditorDropdown';
import { DataProvider } from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/DataProvider';
import type { ProvidedActionsType } from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/DataProvider';
import type { CmlContentInput } from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/types';
import { getRubricId } from 'bundles/grading/utils/formPartUtils';
import { useConfirmBrowserExit } from 'bundles/grading/utils/hooks';
import { isRubricTextBox } from 'bundles/grading/utils/submissionUtils';

type CmlEditorDropdownWrapperProps = {
  editorProps: CmlEditorProps;
  actions: ProvidedActionsType;
};

/**
 * Wraps CmlEditorDropdown.
 * Provides it with CmlEditor Props coming from MultiLineInput
 * Implements logic to create/update/delete/get reusable-comments based on user's interaction with CmlEditorDropdown
 *
 * @param props CmlEditorDropdownWrapperProps
 * @returns
 */
export const CmlEditorDropdownWrapper = (props: CmlEditorDropdownWrapperProps) => {
  // using ref instead of state as we don't need to rerender based on these values changes
  const comment = useRef<CmlContentInput>();
  const isSaving = useRef<boolean>(false);
  const { createComment } = props.actions;

  // We rely on the requirement that "reusable comments" is a complementary feature.
  // If "save" fails, we silently ignore it and don't update application state with the error message.
  // Hence, the `saveComment` function is called in two cases:
  // 1 - on browser exit. We keep showing the confirmation dialog until the request is saved
  // 2 - on component unmount. It is called in closure as an effect.
  // Since this effect doesn't change app state, it silently finishes request after component unmounts, e.g. due to route or props change.
  const saveComment = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    async () => {
      if (comment.current && !isSaving.current) {
        isSaving.current = true;
        try {
          await createComment(comment.current);
          comment.current = undefined;
        } catch (err) {
          // silently ignore reusable comments errors
          // eslint-disable-next-line no-console
          console.error(err);
        } finally {
          isSaving.current = false;
        }
      }
    }, // createComment function is being reset on every parent rerender, but we know it doesn't change inside for this component render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const confirmExit = useConfirmBrowserExit({ onTrigger: saveComment });

  const handleReusableCommentChange = async (cml: CmlContentInput) => {
    comment.current = cml;
    confirmExit.setEnabled(true);
  };

  const handleGetList = async (filterCml?: CmlContentInput) => {
    const res = await props.actions.refetchComments(filterCml);
    return res.data?.GradingComments?.queryOwnComments.edges;
  };

  const handleDelete = async (id: string) => {
    try {
      await props.actions.deleteCommentUsages(id);
      return true;
    } catch {
      return false;
    }
  };

  const handleCommentReuse = (id: string) => {
    props.actions.createCommentUsage(id);
  };

  useEffect(() => {
    return () => {
      saveComment();
    };
  }, [saveComment]);

  return (
    <CmlEditorDropdown
      editorProps={{ ...props.editorProps }}
      onReusableCommentChange={handleReusableCommentChange}
      onCommentReuse={handleCommentReuse}
      getItemList={handleGetList}
      deleteItem={handleDelete}
    />
  );
};

type CmlEditorWithSuggestionsProps = {
  editorProps: CmlEditorProps;
};

/**
 * Renders CmlEditor with suggestions containing the most recent reusable comments.
 * This component is expected to be used in MultiLineInput form-part of `compound-assessments` and only withing SGA Quick Grader context.
 *
 * @param props {editorProps: CmlEditorProps}
 * @returns React Component
 */
export const CmlEditorWithSuggestions = (props: CmlEditorWithSuggestionsProps) => {
  const context = useContext(SGAPageContext);
  if (!context || isEmpty(context)) {
    throw new Error('CmlEditorWithSuggestions must be used within a SGAPageContext.Provider');
  }

  const { submission, submitter } = context.submissionData.data || {};
  const textboxRubric =
    submission?.currentRubric && isRubricTextBox(submission.currentRubric) ? submission.currentRubric : null;

  if (!textboxRubric) {
    return null;
  }

  const rId = getRubricId(textboxRubric);

  // Example of `formElementNodeId`: 'submissionElementId~cnTRviIrEe2-nhLRrac7Rw@2!~STAFF_EVALUATION!~OMysi2XKEe2HEg7PDSZNAQ'
  // `/~([\w]+)@([\d]+)/` results in ["~cnTRviIrEe2-nhLRrac7Rw@2", "cnTRviIrEe2-nhLRrac7Rw", "2"]
  const assignmentVersionId = textboxRubric.formElementNodeId.match(/~(.+)@([\d]+)/);
  const assignmentId = assignmentVersionId ? assignmentVersionId[1] : '';
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const assignmentVersion = assignmentVersionId ? parseInt(assignmentVersionId[2], 10) : 0;

  // ensure component re-instantiates on rubric/submitter change
  const currentKey = `${submitter?.submitterId}~${rId}`;

  return (
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    <DataProvider assignmentId={assignmentId} assignmentVersion={assignmentVersion} reviewPartId={rId}>
      {({ actions }) => {
        return (
          <div>
            <CmlEditorDropdownWrapper editorProps={{ ...props.editorProps }} actions={actions} key={currentKey} />
          </div>
        );
      }}
    </DataProvider>
  );
};
